import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, ChevronRight, Clock, MapPin, MessageCircle, RefreshCw, UserRound } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { useProviderAuth } from '@hooks/useProviderAuth'
import { chatService } from '@lib/supabase/chat.service'
import { providerDashboardService, type ProviderAppointmentItem } from '@lib/supabase/providerDashboard.service'
import { queryKeys } from '@lib/queryKeys'

const ACTIVE_STATUSES = new Set(['scheduled', 'requested'])
const HISTORY_STATUSES = new Set(['completed', 'cancelled', 'rejected'])
const PAGE_SIZE = 5

function formatTime12h(t?: string | null) {
  if (!t || t === '—' || t === 'TBD') return ''
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

function sortChatAppointments(appointments: ProviderAppointmentItem[]) {
  const statusRank: Record<string, number> = {
    scheduled: 0,
    requested: 1,
    completed: 2,
    cancelled: 3,
    rejected: 4,
  }

  return [...appointments].sort((left, right) => {
    const leftStatus = left.status?.toLowerCase() ?? ''
    const rightStatus = right.status?.toLowerCase() ?? ''
    const rankDelta = (statusRank[leftStatus] ?? 99) - (statusRank[rightStatus] ?? 99)
    if (rankDelta !== 0) return rankDelta
    return (right.createdAt || '').localeCompare(left.createdAt || '')
  })
}

function sortSectionByUnread<T extends { id: string; conversationId?: string | null }>(
  appointments: T[],
  unreadConversationKeys: Set<string>,
) {
  return [...appointments].sort((left, right) => {
    const leftUnread = unreadConversationKeys.has(chatService.getConversationKey(left.id, left.conversationId))
    const rightUnread = unreadConversationKeys.has(chatService.getConversationKey(right.id, right.conversationId))

    if (leftUnread === rightUnread) return 0
    return leftUnread ? -1 : 1
  })
}

function ChatAppointmentCard({
  appointment,
  onOpen,
  hasNewMessage,
}: {
  appointment: ProviderAppointmentItem
  onOpen: () => void
  hasNewMessage: boolean
}) {
  const status = appointment.status?.toLowerCase() ?? ''
  const statusStyle: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-700',
    requested: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-red-100 text-red-700',
    rejected: 'bg-red-100 text-red-700',
  }

  return (
    <div className="border border-border bg-card p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-sky-50 text-sky-600">
          {appointment.patientImage ? (
            <img src={appointment.patientImage} alt="" className="h-full w-full object-cover" />
          ) : (
            <UserRound size={20} />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {appointment.patientName || 'Patient'}
              </p>
              <p className="text-xs text-muted-foreground">
                {appointment.serviceType}
              </p>
            </div>
            <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium capitalize', statusStyle[status] ?? 'bg-muted text-muted-foreground')}>
              {appointment.status}
            </span>
          </div>

          <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <Calendar size={12} />
              {new Date(appointment.scheduledDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
            {appointment.scheduledTime ? (
              <div className="flex items-center gap-2">
                <Clock size={12} />
                {formatTime12h(appointment.scheduledTime)}
              </div>
            ) : null}
            {appointment.location ? (
              <div className="flex items-center gap-2 sm:col-span-2">
                <MapPin size={12} />
                <span className="truncate">{appointment.location}</span>
              </div>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-sky-700">
              Chat Consultation
            </span>
            {hasNewMessage ? (
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700">
                New message
              </span>
            ) : null}
            <button
              type="button"
              onClick={onOpen}
              className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              Open Chat
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ProviderChatAppointments() {
  const navigate = useNavigate()
  const { provider } = useProviderAuth()
  const queryClient = useQueryClient()
  const [visibleRecentCount, setVisibleRecentCount] = useState(PAGE_SIZE)
  const [visibleArchivedCount, setVisibleArchivedCount] = useState(PAGE_SIZE)

  const chatAppointmentsQuery = useQuery({
    queryKey: queryKeys.provider.chatAppointments(provider?.id),
    queryFn: () => providerDashboardService.getChatAppointments(),
    enabled: !!provider?.id,
  })

  const latestIncomingMapQuery = useQuery({
    queryKey: queryKeys.provider.latestIncomingMessages(provider?.id),
    queryFn: () => chatService.getLatestIncomingPatientMessageMap(provider!.id),
    enabled: !!provider?.id,
  })

  const appointments = useMemo(
    () => sortChatAppointments(chatAppointmentsQuery.data ?? []),
    [chatAppointmentsQuery.data],
  )

  const unreadConversationKeys = useMemo(() => {
    if (!provider?.id) return new Set<string>()
    return chatService.getUnreadConversationKeys(provider.id, appointments, latestIncomingMapQuery.data ?? {}, 'provider')
  }, [appointments, latestIncomingMapQuery.data, provider?.id])

  const isLoading = chatAppointmentsQuery.isPending
  const isRefreshing = chatAppointmentsQuery.isRefetching || latestIncomingMapQuery.isRefetching
  const error = chatAppointmentsQuery.isError || latestIncomingMapQuery.isError
    ? 'Failed to load chat appointments. Please try again.'
    : null

  useEffect(() => {
    if (!provider?.id) return

    const subscription = chatService.subscribeToProviderIncomingMessages(provider.id, () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.provider.latestIncomingMessages(provider.id) })
    })

    const handleStorage = (event: StorageEvent) => {
      if (event.key && !event.key.startsWith('provider-chat-seen:')) return
      void queryClient.invalidateQueries({ queryKey: queryKeys.provider.latestIncomingMessages(provider.id) })
    }

    window.addEventListener('storage', handleStorage)

    return () => {
      subscription.unsubscribe()
      window.removeEventListener('storage', handleStorage)
    }
  }, [provider?.id, queryClient])

  const handleRefresh = async () => {
    await Promise.all([
      chatAppointmentsQuery.refetch(),
      latestIncomingMapQuery.refetch(),
    ])
  }

  const activeChats = useMemo(
    () => sortSectionByUnread(
      appointments.filter((appointment) => ACTIVE_STATUSES.has(appointment.status?.toLowerCase() ?? '')),
      unreadConversationKeys,
    ),
    [appointments, unreadConversationKeys],
  )

  const recentChats = useMemo(
    () => sortSectionByUnread(
      appointments.filter((appointment) => appointment.status?.toLowerCase() === 'completed'),
      unreadConversationKeys,
    ),
    [appointments, unreadConversationKeys],
  )

  const archivedChats = useMemo(
    () => sortSectionByUnread(
      appointments.filter((appointment) => {
        const status = appointment.status?.toLowerCase() ?? ''
        return HISTORY_STATUSES.has(status) && status !== 'completed'
      }),
      unreadConversationKeys,
    ),
    [appointments, unreadConversationKeys],
  )

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Chats</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Reopen active and recent patient chat consultations in one place.
          </p>
        </div>
        <button
          title="Refresh chat appointments"
          onClick={() => { void handleRefresh() }}
          disabled={isRefreshing}
          className="rounded-lg p-2 transition-colors hover:bg-muted"
        >
          <RefreshCw size={18} className={clsx('text-muted-foreground', isRefreshing && 'animate-spin')} />
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="h-40 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">{error}</p>
          <button type="button" onClick={() => { void handleRefresh() }} className="mt-3 text-sm font-medium text-primary hover:underline">
            Retry
          </button>
        </div>
      ) : appointments.length === 0 ? (
        <div className="border border-dashed border-border bg-card p-10 text-center">
          <MessageCircle size={34} className="mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No chat consultations yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Accepted chat consultations will appear here for quick reopening.</p>
        </div>
      ) : (
        <>
          {activeChats.length > 0 ? (
            <section className="space-y-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">Active</h2>
                <p className="mt-1 text-sm text-muted-foreground">Open patient conversations that still need attention.</p>
              </div>
              <div className="space-y-3">
                {activeChats.map((appointment) => (
                  <ChatAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    hasNewMessage={unreadConversationKeys.has(chatService.getConversationKey(appointment.id, appointment.conversationId))}
                    onOpen={() => navigate(`/provider/chat/${appointment.id}`, { state: { backPath: '/provider/chats' } })}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {recentChats.length > 0 ? (
            <section className="space-y-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">Recent</h2>
                <p className="mt-1 text-sm text-muted-foreground">Completed chat consultations you may need to revisit.</p>
              </div>
              <div className="space-y-3">
                {recentChats.slice(0, visibleRecentCount).map((appointment) => (
                  <ChatAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    hasNewMessage={unreadConversationKeys.has(chatService.getConversationKey(appointment.id, appointment.conversationId))}
                    onOpen={() => navigate(`/provider/chat/${appointment.id}`, { state: { backPath: '/provider/chats' } })}
                  />
                ))}
              </div>
              {recentChats.length > visibleRecentCount && (
                <button
                  type="button"
                  onClick={() => setVisibleRecentCount((c) => c + PAGE_SIZE)}
                  className="w-full rounded-lg border border-border bg-card py-2.5 text-sm font-medium text-primary hover:bg-muted transition-colors"
                >
                  Show {Math.min(PAGE_SIZE, recentChats.length - visibleRecentCount)} more
                </button>
              )}
            </section>
          ) : null}

          {archivedChats.length > 0 ? (
            <section className="space-y-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">Archived</h2>
                <p className="mt-1 text-sm text-muted-foreground">Cancelled and rejected chat consultations.</p>
              </div>
              <div className="space-y-3">
                {archivedChats.slice(0, visibleArchivedCount).map((appointment) => (
                  <ChatAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    hasNewMessage={unreadConversationKeys.has(chatService.getConversationKey(appointment.id, appointment.conversationId))}
                    onOpen={() => navigate(`/provider/chat/${appointment.id}`, { state: { backPath: '/provider/chats' } })}
                  />
                ))}
              </div>
              {archivedChats.length > visibleArchivedCount && (
                <button
                  type="button"
                  onClick={() => setVisibleArchivedCount((c) => c + PAGE_SIZE)}
                  className="w-full rounded-lg border border-border bg-card py-2.5 text-sm font-medium text-primary hover:bg-muted transition-colors"
                >
                  Show {Math.min(PAGE_SIZE, archivedChats.length - visibleArchivedCount)} more
                </button>
              )}
            </section>
          ) : null}
        </>
      )}
    </div>
  )
}